import React from 'react';
import { Modal, Tag, Image, Spin, Empty, Alert, Divider } from 'antd';
import { EyeOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useGetProductByIdQuery, useGetProductsQuery } from '@/services/api/product/productApi';

/**
 * View Product Modal Component
 * Displays product details in a read-only format
 */
const ViewProductModal = ({ visible, onCancel, productId }) => {
  // Fetch product data using the API
  const { 
    data: productData, 
    isLoading, 
    error,
    isFetching 
  } = useGetProductByIdQuery(productId, {
    skip: !visible || !productId
  });

  // Fetch all products to get parent product name
  const { data: products = [] } = useGetProductsQuery();

  // Extract product - API returns: { status: true, data: { original: {...} } }
  const product = productData?.data?.original || productData?.original || productData;

  // Helper Functions
  const formatPrice = (amount) => {
    if (!amount) return '₦0';
    return `₦${parseFloat(amount).toLocaleString('en-NG', { 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2 
    })}`;
  };

  const getAddOnNames = (addons) => {
    if (!addons || addons.length === 0) return 'None';
    return addons.map(addon => addon.name || addon.side_name || `Add-on ${addon.id}`).join(', ');
  };

  const getLocationNames = (locations) => {
    if (!locations || locations.length === 0) return 'No locations assigned';
    return locations.map(loc => loc.name).join(', ');
  };

  const getVariantProductName = () => {
    // Check if we have the parent product object directly
    const variantProduct = product?.variant_product || product?.parent_product;
    
    if (variantProduct) {
      return variantProduct.name || `Product #${variantProduct.id}`;
    }
    
    // If we only have the ID, look it up in the products list
    if (product?.variant_product_id) {
      const parentProduct = products?.data?.find(p => p.id === product.variant_product_id);
      if (parentProduct) {
        return parentProduct.name;
      }
      return `Product ID: ${product.variant_product_id}`;
    }
    
    return 'None';
  };

  // Reusable Field Component
  const DetailField = ({ label, value, className = '' }) => (
    <div className={`${className}`}>
      <label className="block text-xs font-medium text-gray-600 mb-1.5">
        {label}
      </label>
      <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-white text-gray-900 text-sm">
        {value || 'N/A'}
      </div>
    </div>
  );

  // Don't render modal if no productId
  if (!productId) {
    return null;
  }

  // Modal Content Renderers
  const renderError = () => (
    <div className="py-8">
      <Alert
        message="Error Loading Product"
        description={
          <div>
            <p>{error?.data?.message || error?.message || 'An unknown error occurred while fetching product details.'}</p>
            <p className="text-xs text-gray-500 mt-2">Product ID: {productId}</p>
          </div>
        }
        type="error"
        showIcon
      />
    </div>
  );

  const renderEmpty = () => (
    <div className="py-8">
      <Empty 
        description="No product data available"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    </div>
  );

  const renderProductDetails = () => (
    <div>
      {/* PRODUCT IMAGE SECTION */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Product Image
        </h3>
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="flex justify-center">
            {product?.image?.url || product?.product_image ? (
              <Image
                src={product?.image?.url || product?.product_image}
                alt={product?.name || 'Product'}
                className="rounded-lg shadow-sm"
                style={{ maxHeight: '200px', objectFit: 'cover', width: 'auto' }}
                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik"
              />
            ) : (
              <div className="w-32 h-32 rounded-lg bg-gray-200 flex items-center justify-center">
                <span className="text-gray-400 text-5xl">📦</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <Divider className="my-4" />

      {/* BASIC INFORMATION SECTION */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Basic Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
          <DetailField label="Product Name" value={product?.name} />
          <DetailField label="Category" value={product?.category?.name} />
          
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Description
            </label>
            <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-white text-gray-900 text-sm min-h-[60px]">
              {product?.description || 'No description provided'}
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Ingredients
            </label>
            <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-white text-gray-900 text-sm min-h-[60px]">
              {product?.ingredients || 'No ingredients listed'}
            </div>
          </div>
        </div>
      </div>

      <Divider className="my-4" />

      {/* PRODUCT VARIANT SECTION */}
      {(product?.is_variant === 1 || product?.is_variant === true || product?.variant_product || product?.variant_product_id) && (
        <>
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Product Variant
            </h3>
            <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <span className="text-sm text-gray-700 font-medium block">
                    This is a variant product
                  </span>
                  <span className="text-xs text-gray-500">
                    This product is a different version of another dish
                  </span>
                </div>
                <Tag color="purple" className="px-3 py-1">
                  Variant
                </Tag>
              </div>
              
              {(product?.variant_product || product?.variant_product_id || product?.parent_product) && (
                <div className="mt-3 pt-3 border-t border-purple-200">
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Parent Product
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 px-3 py-2 border border-purple-300 rounded-md bg-white text-gray-900 text-sm">
                      {getVariantProductName()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <Divider className="my-4" />
        </>
      )}

      {/* PRICING & INVENTORY SECTION */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Pricing & Inventory
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
          <DetailField 
            label="Price (per unit)" 
            value={product?.amount ? formatPrice(product.amount) : 'N/A'} 
          />
          <DetailField 
            label="Measurement Unit" 
            value={product?.unit_of_measurement?.name || 'N/A'}
          />
          <DetailField 
            label="Current Stock" 
            value={product?.current_qty ?? 0}
          />
          <DetailField 
            label="Minimum Stock Level" 
            value={product?.minimum_qty ?? 0}
          />
          {product?.serving_size && (
            <DetailField label="Serving Size" value={product.serving_size} />
          )}
          <DetailField 
            label="Available Locations" 
            value={getLocationNames(product?.locations)} 
          />
        </div>
      </div>

      <Divider className="my-4" />

      {/* DISCOUNT SECTION */}
      {product?.discount && product.discount > 0 ? (
        <>
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Discount
            </h3>
            <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 font-medium">Discount Amount</span>
                <span className="text-lg font-bold text-orange-600">
                  {formatPrice(product.discount)}
                </span>
              </div>
            </div>
          </div>
          <Divider className="my-4" />
        </>
      ) : null}

      {/* ADD-ONS SECTION */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Add-Ons
        </h3>
        <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
          <label className="block text-xs font-medium text-gray-600 mb-1.5">
            Available Add-Ons
          </label>
          <div className="text-sm text-gray-900">
            {product?.addons && product.addons.length > 0 ? (
              <div className="flex flex-wrap gap-2 mt-2">
                {product.addons.map((addon, index) => (
                  <Tag key={index} color="purple" className="px-3 py-1">
                    {addon.name || addon.side_name || `Add-on ${addon.id}`}
                  </Tag>
                ))}
              </div>
            ) : (
              <span className="text-gray-500">No add-ons available</span>
            )}
          </div>
        </div>
      </div>

      <Divider className="my-4" />

      {/* PRODUCT OPTIONS SECTION */}
      <div className="mb-2">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Product Options
        </h3>
        <div className="space-y-3">
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <div className="flex justify-between items-center">
              <div>
                <span className="text-sm text-gray-700 font-medium block">
                  Can be used as an add-on
                </span>
                <span className="text-xs text-gray-500">
                  {product?.can_be_addon === 1 || product?.can_be_addon === true
                    ? 'This product can be added to other products'
                    : 'This product cannot be used as an add-on'}
                </span>
              </div>
              <div>
                {product?.can_be_addon === 1 || product?.can_be_addon === true ? (
                  <CheckCircleOutlined className="text-2xl text-green-500" />
                ) : (
                  <CloseCircleOutlined className="text-2xl text-gray-400" />
                )}
              </div>
            </div>
          </div>

          <div className={`p-3 rounded-lg border ${
            product?.status === 1 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex justify-between items-center">
              <div>
                <span className="text-sm text-gray-700 font-medium block">
                  Product Status
                </span>
                <span className="text-xs text-gray-500">
                  {product?.status === 1 
                    ? 'This product is visible to customers'
                    : 'This product is hidden from customers'}
                </span>
              </div>
              <div>
                <Tag 
                  color={product?.status === 1 ? 'green' : 'red'}
                  className="px-3 py-1.5 text-sm font-medium"
                >
                  {product?.status === 1 ? 'Active' : 'Inactive'}
                </Tag>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      title={
        <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-200 -mx-6 -mt-5 mb-3 bg-gradient-to-r from-blue-50 to-white">
          <EyeOutlined className="text-blue-500 text-lg" />
          <span className="text-base font-semibold text-gray-800">View Product Details</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={
        <div className="flex justify-end px-6 py-3 border-t border-gray-200 -mx-6 -mb-5 bg-gray-50">
          <button
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      }
      width={900}
      destroyOnClose
      centered
      className="view-product-modal"
      styles={{
        body: { 
          maxHeight: "calc(100vh - 240px)", 
          overflowY: "auto",
          paddingTop: "12px",
          paddingBottom: "12px"
        },
      }}
    >
      <Spin spinning={isLoading || isFetching}>
        <div className="px-2">
          {error && renderError()}
          {!error && !isLoading && !product && renderEmpty()}
          {!error && product && renderProductDetails()}
        </div>
      </Spin>
    </Modal>
  );
};

export default ViewProductModal;